import type { ReasoningEffortOverride, ReasoningMode } from "@/stores/ai-explorer-store"

import { classifyAutoEffort } from "@/features/chat/utils/auto-effort-classifier"

export function getLastQuestionForRequest(
  question: string,
  currentFiles: File[],
  requestFiles: File[] | undefined
): [string, File[]] {
  return [question, requestFiles ?? currentFiles]
}

export function getRetryRequestForLastQuestion(
  lastQuestion: [string, File[]],
  agentId: string | undefined
): { question: string; files: File[]; agentId?: string; isRetry: true } {
  return {
    question: lastQuestion[0],
    files: lastQuestion[1],
    agentId,
    isRetry: true,
  }
}

/**
 * Re-validate the reasoning effort right before it goes on the wire.
 *
 * A stale in-session override (or reasoning-mode map value) can point at an
 * effort the server has since disallowed for the selected model — e.g. an
 * admin disables a level while the tab is open, before the reasoning-mode
 * pill's own clearing effect catches up. Sending that stale value through
 * would reach `prepare_context` and surface as a raw `PermissionError` chat
 * bubble instead of degrading gracefully, so fall back to the model's own
 * default_effort (already clamped server-side, so always valid) when the
 * requested value isn't in the model's current supported_efforts (#2667).
 *
 * When `supportedEfforts` is unknown (model not yet resolved) or the
 * requested value is falsy, the requested value passes through unchanged.
 */
export function resolveReasoningEffortForRequest(
  requestedEffort: string | null | undefined,
  supportedEfforts: string[] | undefined,
  defaultEffort: string | null | undefined
): string | undefined {
  if (!supportedEfforts || !requestedEffort) return requestedEffort || undefined
  if (supportedEfforts.includes(requestedEffort)) return requestedEffort
  return defaultEffort || undefined
}

/**
 * Effort precedence shared by both request builders (legacy `getChatResponse`
 * and the durable `buildDurableChatTurnRequest`): (1) an explicit slider /
 * slash-preset override, (2) an explicit non-auto reasoning mode (fast,
 * thinking, big_brain), (3) for GENERIC Auto specifically — `effectiveModel`
 * resolved to the `"auto"` catalog entry, not an explicit/project model —
 * the complexity heuristic, (4) otherwise the resolved model's own
 * (already access-clamped) `default_effort`, falling back to the heuristic
 * when that's unset.
 *
 * Step (3) must check the resolved model id, not just `reasoningMode`: Auto
 * mode can still resolve to an explicit project-default model, whose
 * `default_effort` is a deliberate choice to preserve. But when Auto
 * resolves to the generic `"auto"` model, that catalog entry's own
 * `default_effort` describes whatever reasoning model Auto happens to route
 * to server-side — inheriting it here would silently defeat the low/medium
 * complexity policy for ordinary prompts (issue #2893).
 */
export function resolveRequestedReasoningEffort(params: {
  effortOverride: ReasoningEffortOverride | null
  reasoningMode: ReasoningMode
  reasoningEffort: ReasoningEffortOverride
  effectiveModelId: string | undefined
  effectiveModelDefaultEffort: string | undefined
  question: string
  fileCount: number
  locale: string
}): ReasoningEffortOverride {
  const {
    effortOverride,
    reasoningMode,
    reasoningEffort,
    effectiveModelId,
    effectiveModelDefaultEffort,
    question,
    fileCount,
    locale,
  } = params

  // Generic Auto: the server-side router owns both model and effort, and the
  // UI no longer offers a slider there — a lingering override from an earlier
  // model pick must not be sent as if it were a deliberate choice.
  const genericAuto = reasoningMode === "auto" && effectiveModelId === "auto"
  if (effortOverride && !genericAuto) return effortOverride
  if (reasoningMode !== "auto") return reasoningEffort
  if (genericAuto) return classifyAutoEffort(question, fileCount, locale)
  return (
    (effectiveModelDefaultEffort as ReasoningEffortOverride | undefined) ||
    classifyAutoEffort(question, fileCount, locale)
  )
}
